import React from "react";
import { Row, Col, Table } from "react-bootstrap";
import GraficoZScore from "./GraficoZScore";
import GraficoZScoreAF from "./GraficoZScoreAF";
import GraficoEC from "./GraficoEC";
import GraficoDonutJuicio from "./GraficoDonutJuicio";

// Descripciones de cada variable/alerta. Compartidas entre el reporte (fila
// expandible) y la ficha detallada del evaluado.
const _descripcion = {
  // Fortalezas / Antifortalezas
  Optimismo:
    "Un operador con alta puntuación en optimismo tenderá a esperar resultados positivos y a mantener una actitud favorable ante los desafíos laborales. Esto facilita la motivación, la perseverancia y la disposición a enfrentar situaciones difíciles con una perspectiva constructiva.",
  Esperanza:
    "Un operador con alta puntuación en esperanza tendrá la capacidad de establecer metas claras, generar rutas para alcanzarlas y mantener la motivación necesaria para persistir ante obstáculos. Esto favorece la planificación y la orientación al logro.",
  Autoeficacia:
    "Un operador con alta puntuación en autoeficacia confiará en su capacidad para organizar y ejecutar las acciones necesarias para alcanzar los logros deseados. Esto implica una adecuada organización de sub-habilidades cognitivas, sociales, emocionales y conductuales.",
  Resiliencia:
    "Un operador con alta puntuación en resiliencia tendrá la capacidad de enfrentar y recuperarse frente a problemas o adversidades. Exhibirá fuerza, resistencia y capacidad de acción ante situaciones difíciles.",
  Prudencia:
    "Da por sentado aquello que ignora, sin reflexionar antes de cada decisión importante, pudiendo sobrestimar sus conocimientos y buscar resolver por cuenta propia",
  Autorregulación:
    "Falta de autoconocimiento  para poder predecir cómo va a responder ante ciertas situaciones, sin desplegar capacidad de modular sus reacciones",
  Responsabilidad:
    "Actúa sin comprender el impacto de sus actos, ni el valor de la vida y los vínculos, como para actuar con seguridad y responsabilidad",
  Vitalidad:
    "Baja fuerza interior y disposición de salud física con la cual enfrento el día a día y lidio con la adversidad, sin lograr sobreponerme",
  Apertura:
    "Incapacidad de cuestionar las propias premisas, y gestionar el sesgo de confirmación, sin lograr gestionar las creencias operantes",

  // Riesgos
  "Exceso de Confianza":
    "Facilita la conducta de no verificación. Implica riesgos altos, que puede tener efectos negativos en el ciclo de gestión de riesgos, específicamente en el control de la ejecución de su trabajo y en la detección de situaciones y condiciones que pueden afectar la ejecución de tareas a realizar. Puede llevar al operador a considerar que domina las habilidades y subestimar peligros, tomar decisiones precipitadas, omitir medidas de seguridad y no prestar la debida atención.",
  Pesimismo:
    "Un operador con puntuación alta en pesimismo tendrá dificultades en resolución activa de problemas y en prestar atención a información relevante para solucionarlo.",
  Desesperanza:
    "Un operador con puntuación alta en desesperanza tendrá bajo nivel de energía dirigida hacia el logro de un objetivo concreto. Puede verse involucrado en accidentes laborales por desconcentración, errores por dificultad para prestar atención.",
  Ineficacia:
    "Un operador con alta puntuación en ineficacia desconfiará de su propia capacidad de organizar y ejecutar las acciones necesarias para alcanzar los logros deseados. En la autoeficacia deben organizarse y desplegarse de manera efectiva sub-habilidades cognitivas, sociales, emocionales y conductuales, las cuales fallan en una persona que se percibe como ineficaz, creyendo que no será capaz de realizar una determinada actividad.",
  Fragilidad:
    "Un operador con alta puntuación en fragilidad le costará enfrentar y recuperarse frente a un problema o adversidad. Exhibirá fuerza, resistencia y acción reducidas.",
  Cinismo:
    "La actitud de cinismo comprende pensamientos y sentimientos de que el cambio sólo ocurre a nivel de discurso y que está mal planeado. Propensión por fingir que se está de acuerdo con las modificaciones necesarias cuando en realidad se obstaculizan cada vez que es posible. Un operador con alta puntuación en cinismo dirá que sí apoya un cambio, pero, está pensando en no hacerlo.",
  Superstición:
    "factor predictor de conductas irracionales, donde la persona nublara su razonamiento en relación con una explicación con un componente mágico o inexplicable",
  "Influencia Social":
    "La influencia de otros puede incidir en el comportamiento de las personas frente al riesgo.   Las creencias actitudes, intenciones y conductas dependen en buena medida de las relaciones que la persona mantenga con otros individuos, grupos o instituciones o de la presencia real, imaginada o implícita de otros.",
  "Sobre valor de sí mismo":
    "Los sesgos que conducen al exceso de confianza generan la sobrestimación de las habilidades propias, del conocimiento propio, de las posibilidades de éxito y del grado de control sobre los sucesos y las situaciones, introduciendo un riesgo.",
  Juicio:
    "Toma decisiones ante escenarios operacionales introduciendo incertidumbre y generando riesgos críticos en la operación, incurriendo en una desviación conductual",

  // Alertas
  "Distorsión Motivacional":
    "Modificación de las respuestas de un postulante para alinearlas con un resultado deseado, no con su autopercepción real, puede deberse a intento deliberado de manipulación, negación de ciertas tendencias o a bajo nivel de comprensión y/o atención de los ítems presentados.",
  "Evasión Conductual":
    "La persona tiende a marcar el centro, es decir no tiene claridad de cómo conducirse o comportarse ante las preguntas, lo que introduce incertidumbre comportamental, generando un riesgo. ",
};

// Contenido de los cálculos de un evaluado: tabla resumen + hallazgos + gráficos.
// Es exactamente el bloque que se muestra en la fila expandible del reporte; se
// extrajo a este componente para reutilizarlo en la ficha detallada (perfil 3).
const DetalleCalculosEvaluado = ({ item, totales, norma }) => {
  if (!item) return null;

  return (
    <div style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <Row>
        <Col xs={12} md={5}>
          <Table size="sm" bordered style={{ marginBottom: 0 }}>
            <tbody>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">RUT</td>
                <td className="py-1">{item.rut}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Resultado</td>
                <td className="py-1">{item.RESULTADO}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Total Riesgos</td>
                <td className="py-1">{item.Total_Riesgo}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Juicio (JUI)</td>
                <td className="py-1">{item.JUI || "Sin Riesgo"}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Anti Fortaleza</td>
                <td className="py-1">{item.Total_AF}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Distorsión Motivacional</td>
                <td className="py-1">{item.Total_DM}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Evasión Conductual</td>
                <td className="py-1">{item.Total_EC}</td>
              </tr>
              <tr style={{ lineHeight: "1.2" }}>
                <td className="fw-bold py-1">Detalle</td>
                <td className="titulo1 py-1">{item.OBSERVACIONES}</td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col xs={12} md={7}>
          <div
            className="rounded p-3"
            style={{
              maxHeight: "350px",
              overflowY: "auto",
              fontSize: "0.73rem",
            }}
          >
            {(() => {
              const hallazgos = [];

              const riesgoMap = [
                { campo: "ECO", nombre: "Exceso de Confianza" },
                { campo: "PES", nombre: "Pesimismo" },
                { campo: "DES", nombre: "Desesperanza" },
                { campo: "INE", nombre: "Ineficacia" },
                { campo: "FRA", nombre: "Fragilidad" },
                { campo: "CIN", nombre: "Cinismo" },
                { campo: "SUP", nombre: "Superstición" },
                { campo: "ISO", nombre: "Influencia Social" },
                { campo: "SSV", nombre: "Sobre valor de sí mismo" },
              ];
              riesgoMap.forEach(({ campo, nombre }) => {
                if (item[campo] === "R") {
                  hallazgos.push({
                    tipo: "Riesgo",
                    nombre,
                    descripcion: _descripcion[nombre],
                  });
                }
              });

              if (item.JUI === "R") {
                hallazgos.push({
                  tipo: "Riesgo",
                  nombre: "Juicio",
                  descripcion: _descripcion["Juicio"],
                });
              }

              const afMap = [
                { campo: "PRU", nombre: "Prudencia" },
                { campo: "AUT", nombre: "Autorregulación" },
                { campo: "RES", nombre: "Responsabilidad" },
                { campo: "VIT", nombre: "Vitalidad" },
                { campo: "APE", nombre: "Apertura" },
              ];
              afMap.forEach(({ campo, nombre }) => {
                if (item[campo] === "D") {
                  hallazgos.push({
                    tipo: "Anti Fortaleza",
                    nombre,
                    descripcion: _descripcion[nombre],
                  });
                }
              });

              if (parseInt(item.Total_DM || 0) > 0) {
                hallazgos.push({
                  tipo: "Alerta",
                  nombre: "Distorsión Motivacional",
                  descripcion: _descripcion["Distorsión Motivacional"],
                });
              }

              if (parseInt(item.Total_EC || 0) > 0) {
                hallazgos.push({
                  tipo: "Alerta",
                  nombre: "Evasión Conductual",
                  descripcion: _descripcion["Evasión Conductual"],
                });
              }

              if (hallazgos.length === 0) {
                return (
                  <span className="text-muted">
                    Sin observaciones relevantes.
                  </span>
                );
              }

              return hallazgos.map((h, idx) => (
                <div
                  key={idx}
                  className={idx > 0 ? "mt-2 pt-2 border-top" : ""}
                >
                  <span
                    className="badge me-2"
                    style={{
                      backgroundColor:
                        h.tipo === "Riesgo"
                          ? "#c0504d"
                          : h.tipo === "Anti Fortaleza"
                            ? "#e6a020"
                            : "#6c757d",
                      fontSize: "0.72rem",
                    }}
                  >
                    {h.tipo}
                  </span>
                  <strong>{h.nombre}</strong>
                  <p className="mb-0 mt-1" style={{ color: "#555" }}>
                    {h.descripcion}
                  </p>
                </div>
              ));
            })()}
          </div>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12} md={6}>
          <h6 className="fw-bold" style={{ fontSize: "0.85rem", color: "#016c99" }}>
            Riesgos: Z-Score
          </h6>
          <div style={{ height: "320px" }}>
            <GraficoZScore item={item} totales={totales} norma={norma} />
          </div>
        </Col>
        <Col xs={12} md={6}>
          <h6 className="fw-bold" style={{ fontSize: "0.85rem", color: "#016c99" }}>
            Antifortalezas: Z-Score
          </h6>
          <div style={{ height: "320px" }}>
            <GraficoZScoreAF item={item} totales={totales} norma={norma} />
          </div>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col xs={12} md={8}>
          <h6 className="fw-bold" style={{ fontSize: "0.85rem", color: "#016c99" }}>
            Evasión Conductual (%)
          </h6>
          <div style={{ height: "250px" }}>
            <GraficoEC item={item} />
          </div>
        </Col>
        <Col xs={12} md={4}>
          <h6 className="fw-bold" style={{ fontSize: "0.85rem", color: "#016c99" }}>
            Riesgo de Juicio
          </h6>
          <div style={{ height: "250px" }}>
            <GraficoDonutJuicio item={item} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DetalleCalculosEvaluado;
